import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { ExpenseCategoryInfoSetupFieldsPanel } from "./Panels/ExpenseCategoryInfoSetupFieldsPanel";
import { ExpenseCategoryService } from "../../services/ExpenseCategoryService";
import { ExpenseCategorySetupToolbar } from "./ExpenseCategorySetupToolbar";
import { ExpenseCategorySetupValidationSchema } from "./validator";
import { IAppState } from "../../store/reducers";
import { IExpenseCategory } from "../../models/ExpenseCategory";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IExpenseCategorySetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "ExpenseCategory-single-data-record";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return ExpenseCategoryService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false
	});
const EXPENSE_CATEGORY_LIST_QUERY_KEY = "expenseCategory-list";

const useExpenseCategory = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [EXPENSE_CATEGORY_LIST_QUERY_KEY, params],
		queryFn: (data) => {

			return ExpenseCategoryService.getExpenseCategory(
				clientId,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false
	});


const useGetExpenseCategoryDDLItems = (clientId: string) =>
	useQuery({
		queryKey: ['ExpenseCategory-ddl', clientId],
		queryFn: (data) => {
			return ExpenseCategoryService.getGetExpenseCategoryDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
export const ExpenseCategorySetup: React.FC<IExpenseCategorySetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [ExpenseCategoryId, setExpenseCategoryId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "expenseCategory",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "expenseCategory",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useExpenseCategory(
		clientId,
		queryParams,
		navigateToLogout
	);

	const expenseCategoryList: any | null = data;

	const { isPending: isPendingExpenseCategoryDDLItems, data: expenseCategoryDDLItems } =
		useGetExpenseCategoryDDLItems(clientId);

	const { isPending: isSubmitting, mutate: submitExpenseCategoryFormFields } =
		useMutation({
			mutationFn: ExpenseCategoryService.create,
			onSuccess: (res: IExpenseCategory | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIExpenseCategoryResponse(res)) {
					// setEditRecordId(res.id);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] });
					queryClient.invalidateQueries({ queryKey: [EXPENSE_CATEGORY_LIST_QUERY_KEY] });
					let path = `/company/expense-category/setup/${res.data.id}`;
					navigate(path);
				} else if (res.status === 201) {
					// if (instanceOfIExpenseCategoryResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] });
					queryClient.invalidateQueries({ queryKey: [EXPENSE_CATEGORY_LIST_QUERY_KEY] });
					setEditRecordId(res.data.id)
					let path = `/company/expense-category/setup/${res.data.id}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const upadtedExpenseCategoryListItems = useMemo(() => {
		return (
			expenseCategoryList &&
			expenseCategoryList.items &&
			expenseCategoryList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/company/expense-category/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.expenseCategory}
					</Link>
				),
			}))
		);
	}, [expenseCategoryList]);

	const onExpenseCategorySelectionChangeHandler = (selected: any) => {
		if (selected.value) {
			setEditRecordId(selected.value)
			navigate(`/company/expense-category/setup/${selected.value}`)
		} else {
			setEditRecordId(undefined);
			formMethods.reset();
			navigate(`/company/expense-category/setup`);
		}
	}

	const DEFAULT_VALUES = {
		code: undefined,
		expenseCategory: undefined,
		notes: undefined,
	};


	const formMethods = useForm<any>({
		resolver: yupResolver(ExpenseCategorySetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			expenseCategory: values.expenseCategory,
			code: values.code,
			notes: values.notes !== "" ? values.notes : undefined,
		};
		submitExpenseCategoryFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteExpenseCategoryById } =
		useMutation({
			mutationFn: ExpenseCategoryService.delExpenseCategory,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the ExpenseCategory fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/company/expense-category/setup`;
				navigate(path);
				queryClient.invalidateQueries({ queryKey: [EXPENSE_CATEGORY_LIST_QUERY_KEY] });
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteExpenseCategoryField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				ExpenseCategoryId: ExpenseCategoryId,
				navigateToLogout: navigateToLogout,
			};
			deleteExpenseCategoryById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setExpenseCategoryId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setExpenseCategoryId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveExpenseCategoryById } =
		useMutation({
			mutationFn: ExpenseCategoryService.archiveExpenseCategory,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the ExpenseCategory fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/company/expenseCategory/setup`;
				navigate(path);
				return queryClient.invalidateQueries({ queryKey: [EXPENSE_CATEGORY_LIST_QUERY_KEY] });
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveExpenseCategoryField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				expenseCategoryId: ExpenseCategoryId,
				navigateToLogout: navigateToLogout,
			};
			archiveExpenseCategoryById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setExpenseCategoryId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/company/expense-category/setup`;
		navigate(path);
	};

	const onRowEditClicked = useCallback((params: IExpenseCategory | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/company/expense-category/setup/${params.value}`;
		navigate(path);
	}, []);

	const expandedLabels = ["Company", "Expense Category"];
	const expandedExpenseCategorySetupOrSetupItem = "Expense Category"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Expense Category" &&
				item.label === expandedExpenseCategorySetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedExpenseCategorySetupOrSetupItem && "/company/expense-category/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		ExpenseCategoryName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{ pb: "5rem", "& .MuiAppBar-root": { zIndex: 2, top: "65px" } }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isPendingExpenseCategoryDDLItems}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteExpenseCategoryField}
				cancelActionHandler={onCloseHandler}
				testId="ExpenseCategory-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="ExpenseCategory"
										testId={`ExpenseCategory`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<ExpenseCategoryInfoSetupFieldsPanel
											expenseCategoryDDLItems={expenseCategoryDDLItems} onExpenseCategorySelectionChangeHandler={onExpenseCategorySelectionChangeHandler}
										/>
									</StandardPanel>
								}
								toolbar={
									<ExpenseCategorySetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="bottom"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									expenseCategoryList && upadtedExpenseCategoryListItems
										? upadtedExpenseCategoryListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									expenseCategoryList && expenseCategoryList.total
										? expenseCategoryList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveExpenseCategoryField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"ExpenseCategory-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
